import { Injectable } from '@angular/core';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../external-interfaces/notification-provider.service';
import { takeUntil } from 'rxjs/operators';
import { CartDetailsSourceIdsEnum } from '../models/cartDetails/data-source-ids.enum';
import { BehaviorSubject, Subject } from 'rxjs';
import { TrafficTypeEnum } from '../models/cartDetails/trafficType.model';
import { ProcessTypeEnum } from '../models/cartDetails/process-type.enum';
import { ServiceTypeEnum } from '../models/cartDetails/service-type.enum';
import { TaxNameEnum } from '../models/cartDetails/tax-name.enum';
import { IBundle, IDevice, IPrice, IPriceCycle, ISummaryPrice } from '../interfaces/cart-details.interface';
import { ConfigService } from '../cart-process/config.service';
import { CommonInfoService } from '../cart-process/common-info.service';
import SlotModel from '../models/slot.model';
import { IOrderSummary } from '../interfaces/order-summary.interface';
import { ProductDevice, ProductOffer } from '../interfaces/data-layer.interface';

declare let window: any;

class DataLayerDeviceService {
  public isGadget: boolean;
  public uid: null = null;
  public sku: string;
  public bundleNumber: number;
  public offerId: string;
  public name: string;
  public price: IPrice;
  public pricePerPiece: string;
  public oneTimeFee: IPrice;
  public quantity: number;
  public brand: string;
  public color: string;
  public bundledDevice: boolean;
  public processType: ProcessTypeEnum;
  public brandType: string;
  public installments: IPriceCycle[];
  public deviceTermLength?: number;

  constructor(bundle: IBundle, accessory: any, isGadget: boolean = false) {
    this.bundleNumber = bundle.number;
    this.offerId = bundle.offer.offerId;
    this.processType = bundle.processType;
    this.isGadget = isGadget;
    this.sku = accessory.sku;
    this.name = accessory.name;
    this.price = bundle.offer?.newContractPrices?.prices?.find(item => +item.price[TaxNameEnum.GROSS] > 0)?.price;
    this.pricePerPiece = accessory.pricePerPiece;
    this.oneTimeFee = accessory.price;
    this.quantity = accessory.quantity;
    this.brand = accessory.brand || '';
    this.color = accessory.color || '';
    this.bundledDevice = accessory.bundledDevice;
    this.brandType = window.HELPER_DATA.brand;
    this.installments = bundle.offer.newContractPrices.installments;
    this.deviceTermLength = bundle.offer.deviceTermLength;
  }
}

class DataLayerSlotService {
  public sku: null = null;
  public uid: string;
  public bundleNumber: number;
  public slotNo: number;
  public offerId: string;
  public offerName: string;
  public gadgets: DataLayerDeviceService[];
  public device: DataLayerDeviceService | null;
  public monthlyPrices: any[];
  public bundleType: string;
  public trafficType: TrafficTypeEnum;
  public processType: ProcessTypeEnum;
  public serviceType: ServiceTypeEnum;
  public promotionType: string;
  public promotionName: string;
  public termOfServiceNumber: number;
  public brandType: string;
  public tariffName: string;
  public recommendationResponseId: string;

  constructor(bundle: IBundle) {
    this.uid = bundle.uid;
    this.bundleNumber = bundle.number;
    this.slotNo = bundle.slotNo;
    this.offerId = bundle.offer.offerId;
    this.offerName = bundle.offer.offerName;
    this.gadgets = bundle.gadget.map((value: any) => new DataLayerDeviceService(bundle, value, true));
    this.device = bundle.device[0] ? new DataLayerDeviceService(bundle, bundle.device[0], false) : null;
    this.monthlyPrices = bundle.offer.newContractPrices.prices;
    this.bundleType = bundle.bundleType;
    this.processType = bundle.processType;
    this.trafficType = bundle.offer.trafficType;
    this.serviceType = bundle.offer.serviceType;
    this.promotionType = bundle.offer.promotionType;
    this.promotionName = bundle.offer.promotionName;
    this.termOfServiceNumber = bundle.offer.termOfServiceNumber;
    this.tariffName = bundle.offer.tariffName;
    this.recommendationResponseId = bundle.recommendationResponseId;
    this.brandType = window.HELPER_DATA.brand;
  }
}


@Injectable()
export class DataLayerService {
  public data$ = new BehaviorSubject<{[P in CartDetailsSourceIdsEnum]?: DataLayerSlotService[] | DataLayerDeviceService[]}>(null);
  public dataObs$ = this.data$.asObservable();

  public slots: DataLayerSlotService[] = [];
  public internetSlots: DataLayerSlotService[] = [];
  public accessories: DataLayerDeviceService[] = [];

  private sourceIdTokens: { [id in CartDetailsSourceIdsEnum]?: string } = {};

  constructor(
    private notificationProvider: NotificationProviderService,
    private configService: ConfigService,
    private commonInfoService: CommonInfoService
  ) {
    Object.keys(CartDetailsSourceIdsEnum).forEach((key) => {
      this.sourceIdTokens[key] = key;
    });

    this.notificationProvider.subscribe(NotificationProviderTypeEnum.SLOTS_SERVICE, (context: any) => {
      if (context) {
        const sessionCollection = JSON.parse(sessionStorage.getItem('dataLayer'));

        const data = {
          slots: this.slots,
          internetSlots: this.internetSlots,
          accessories: this.accessories,
        };

        sessionStorage.setItem('dataLayer', JSON.stringify(data));

        if (sessionCollection) {
          this.findNewAccessories(sessionCollection.accessories);
        }
      }
    });
  }

  public generateToken(id: CartDetailsSourceIdsEnum): string {
    const randomNumber = Math.floor(Date.now() * Math.random());
    const token = id + '_' + randomNumber;

    this.sourceIdTokens[id] = token;

    return token;
  }

  public getToken(id: CartDetailsSourceIdsEnum): string {
    return this.sourceIdTokens[id];
  }

  public setSlotData(bundle: IBundle): void {
    const newSlot = new DataLayerSlotService(bundle);

    this.slots.push(newSlot);
  }

  public setInternetSlotData(bundle: IBundle): void {
    const newSlot = new DataLayerSlotService(bundle);

    this.internetSlots.push(newSlot);
  }

  public setAccessoryData(bundle: IBundle, accessory: IDevice): void {
    const newSlot = new DataLayerDeviceService(bundle, accessory);

    this.accessories.push(newSlot);
  }

  public resetSlotsData(): void {
    this.slots = [];
    this.internetSlots = [];
    this.accessories = [];
  }

  public onAddOptionalSlot = function fn(): void {
    if (fn['processing']) {
      return;
    }

    fn['processing'] = true;

    const ngUnsubscribe$ = new Subject();
    const tempSlots = this.slots.slice(0);
    const subject$ = this.notificationProvider.getSubject(NotificationProviderTypeEnum.SLOTS_SERVICE);

    subject$
      .pipe(
        takeUntil(ngUnsubscribe$)
      )
      .subscribe((response: any) => {
        if (response && response._sourceToken === this.getToken(CartDetailsSourceIdsEnum.OPTIONAL_MEMBER)) {
          ngUnsubscribe$.next();
          ngUnsubscribe$.complete();

          const slotData = this.diffSlots(tempSlots, this.slots);

          if (slotData.length) {
            this.prepareEvent('add_to_cart', slotData, 'basket');
            this.notifySubscribers(CartDetailsSourceIdsEnum.OPTIONAL_MEMBER, slotData);
          }

          fn['processing'] = false;
        }
      }
    );
  };

  public onAddOptionalInternetSlot = function fn(): void {
    if (fn['processing']) {
      return;
    }

    fn['processing'] = true;

    const ngUnsubscribe$ = new Subject();
    const tempSlots = this.internetSlots.slice(0);
    const subject$ = this.notificationProvider.getSubject(NotificationProviderTypeEnum.SLOTS_SERVICE);

    subject$
      .pipe(
        takeUntil(ngUnsubscribe$)
      )
      .subscribe((response: any) => {
        if (response && response._sourceToken === this.getToken(CartDetailsSourceIdsEnum.OPTIONAL_INTERNET)) {
          ngUnsubscribe$.next();
          ngUnsubscribe$.complete();

          const slotData = this.diffSlots(tempSlots, this.internetSlots);

          if (slotData.length) {
            this.prepareEvent('add_to_cart', slotData, 'basket');
            this.notifySubscribers(CartDetailsSourceIdsEnum.OPTIONAL_INTERNET, slotData);
          }

          fn['processing'] = false;
        }
      }
    );
  };

  public onChangeProcess = function fn(): void {
    if (fn['processing']) {
      return;
    }

    fn['processing'] = true;

    const ngUnsubscribe$ = new Subject();
    const tempSlots = this.slots;
    const subject$ = this.notificationProvider.getSubject(NotificationProviderTypeEnum.SLOTS_SERVICE);

    subject$
      .pipe(
        takeUntil(ngUnsubscribe$)
      )
      .subscribe((response: any) => {
        if (response && response._sourceToken === this.getToken(CartDetailsSourceIdsEnum.CHANGE_PROCESS)) {
          ngUnsubscribe$.next();
          ngUnsubscribe$.complete();

          const slotData = this.diffSlots(tempSlots, this.slots);
          const allItems = this.prepareSlotsData(slotData);

          if (allItems.length) {
            this.cartViewCheckout(this.commonInfoService.summaryPrices, allItems);
            this.notifySubscribers(CartDetailsSourceIdsEnum.CHANGE_PROCESS, allItems);
          }

          fn['processing'] = false;
        }
      }
    );
  };

  public onClearCart(): void {
    const tempSlots = [].concat(this.slots, this.internetSlots, this.accessories);

    const params: any[] = this.prepareSlotsData(tempSlots);

    if (params.length) {
      this.prepareEvent('remove_from_cart', params, 'cart_remove_all');
    }
  }

  public onRemoveOptionalSlot(uid: string): void {
    this.onRemoveSlot(this.slots, uid);
  }

  public onRemoveOptionalInternetSlot(uid: string): void {
    this.onRemoveSlot(this.internetSlots, uid);
  }

  public onRemoveOptionalSlotDevice(uid: string): void {
    this.onRemoveDevice(this.slots, uid);
  }

  public onRemoveOptionalInternetSlotDevice(uid: string): void {
    this.onRemoveDevice(this.internetSlots, uid);
  }

  public deliveryAndPayment(): void {
    const params = {
      event: 'delivery_and_payment',
      decisionAfterCC: window.contractData.innerHTML ? JSON.parse(window.contractData.innerHTML).decisionAfterCC : ''
    };

    this.pushEvent(params);
  }

  public cartViewCheckout(summaryPrices: ISummaryPrice[], apiSlots: SlotModel[], orderSummary?: IOrderSummary.Root): void {
    this.cartViewCheckoutEvent(summaryPrices, apiSlots, this.prepareProductsList(), orderSummary);
  }

  private findNewAccessories(accessories: any): void {
    const slotData = this.diffSlots(accessories, this.accessories, true);

    if (slotData.length) {
      this.notifySubscribers(CartDetailsSourceIdsEnum.ACCESSORY_ADD_NEW, slotData);
    }
  }


  private onRemoveSlot = function fn(slots: any, uid: string): void {
    const ngUnsubscribe$ = new Subject();
    const tempSlots = slots.slice(0);
    const subject$ = this.notificationProvider.getSubject(NotificationProviderTypeEnum.SLOTS_SERVICE);

    subject$
      .pipe(
        takeUntil(ngUnsubscribe$)
      )
      .subscribe((response: any) => {
        if (response && response._sourceToken === this.getToken(CartDetailsSourceIdsEnum.REMOVE_SLOT)) {
          ngUnsubscribe$.next();
          ngUnsubscribe$.complete();

          const slotData = this.findItemById(tempSlots, uid);
          const allItems = this.prepareSlotsData(slotData);

          if (allItems.length) {
            this.prepareEvent('remove_from_cart', allItems, 'basket');
            this.notifySubscribers(CartDetailsSourceIdsEnum.REMOVE_SLOT, allItems);
          }
        }
      }
    );
  };

  private onRemoveDevice = function fn(slots: any, uid: string): void {
    const ngUnsubscribe$ = new Subject();
    const tempSlots = slots.slice(0);
    const subject$ = this.notificationProvider.getSubject(NotificationProviderTypeEnum.SLOTS_SERVICE);

    subject$
      .pipe(
        takeUntil(ngUnsubscribe$)
      )
      .subscribe((response: any) => {
        if (response && response._sourceToken === this.getToken(CartDetailsSourceIdsEnum.REMOVE_DEVICE)) {
          ngUnsubscribe$.next();
          ngUnsubscribe$.complete();

          const slotData = this.findItemById(tempSlots, uid);
          const slotDevice = slotData[0].device;

          if (slotDevice) {
            this.prepareEvent('remove_from_cart', [slotDevice], 'basket');
            this.notifySubscribers(CartDetailsSourceIdsEnum.REMOVE_DEVICE, [slotDevice]);
          }
        }
      }
    );
  };

  private prepareSlotsData(slots: DataLayerSlotService[]): any[] {
    const params: any[] = [];

    slots.forEach((value: DataLayerSlotService, index: number) => {
      params.push(value);

      if (value.device) {
        params.push(value.device);
      }

      if (value.gadgets && value.gadgets.length) {
        value.gadgets.forEach((gadget: any) => params.push(gadget));
      }
    });

    return params;
  }

  private findItemById(items: any[], id: string): object {
    return items.filter((item: any) => item.uid === id || item.sku === id);
  }

  private diffSlots(previous: any[], current: any[], ignoreQty: boolean = false): any[] {
    const filteredData: any[] = current.slice(0);

    for (let i = filteredData.length; i--;) {
      const currentId = filteredData[i].uid || filteredData[i].sku;
      const currentQty = filteredData[i].sku ? filteredData[i].quantity : 1;
      const currentProcessType = filteredData[i].processType;

      for (let j = 0; j < previous.length; j++) {
        const previousId = previous[j].uid || previous[j].sku;
        const previousQty = previous[j].sku ? previous[j].quantity : 1;
        const previousProcessType = previous[j].processType;

        if (currentId === previousId && currentProcessType === previousProcessType && ignoreQty) {
          filteredData.splice(i, 1);
        } else if (currentId === previousId && currentProcessType === previousProcessType && !ignoreQty && currentQty === previousQty) {
          filteredData.splice(i, 1);
        }
      }
    }

    return filteredData;
  }

  private notifySubscribers(
    _id: CartDetailsSourceIdsEnum,
    _data: DataLayerSlotService[] | DataLayerDeviceService[]
  ): void {
    const data: {[P in CartDetailsSourceIdsEnum]?: DataLayerSlotService[] | DataLayerDeviceService[]}  =  {[_id]: _data};

    this.data$.next(data);
  }

  private prepareProductsList(): (ProductDevice | ProductOffer)[] {
    const tempSlots: any[] = [].concat(this.slots, this.internetSlots, this.accessories);
    const slotData: any[] = this.prepareSlotsData(tempSlots);
    const productsList: (ProductDevice | ProductOffer)[] = [];

    slotData.forEach((value: any) => {
      let product: ProductDevice | ProductOffer;
      let processTypeName = value.processType;

      if (value.processType === ProcessTypeEnum.OTHER_SALES) {
        processTypeName = 'OTHER_SALES';
      } else if (value.processType === ProcessTypeEnum.PREPAID_2_POSTPAID) {
        processTypeName = 'PREPAID2POSTPAID';
      }

      if (value.sku) {
        const sku = this.getItemId(value.sku);
        const variant = this.getItemColor(value.sku);
        const brand = this.getItemBrand(value.sku);
        const installmentPrice = value.installments?.filter(item => item.price);
        const installmentPriceMax = installmentPrice
          ? Math.max(...installmentPrice?.map(item => +item.price[this.commonInfoService.taxName]))
          : 0;
        const price = installmentPriceMax ? installmentPriceMax : +value.oneTimeFee[this.commonInfoService.taxName];
        const oneTimeFee = price === +value.oneTimeFee[this.commonInfoService.taxName]
          ? 0
          : +value.oneTimeFee[this.commonInfoService.taxName];

        product = {
          item_id: sku,
          item_name: value.name,
          currency: 'PLN',
          item_brand: brand,
          item_category: 'phone',
          item_variant: variant,
          price: price ? price : 0,
          one_time_fee: oneTimeFee,
          quantity: +value.quantity,
          commitment: value.deviceTermLength ? `${value.deviceTermLength}` : null,
          process_type: processTypeName.toLowerCase()
        };
      } else {
        const offerPrice = Math.max(...value.monthlyPrices.map(item => item.price[this.commonInfoService.taxName]));

        product = {
          item_id: value.offerId,
          item_name: value.offerName,
          currency: 'PLN',
          item_category: `${value.bundleType}/${value.trafficType}/${value.serviceType}`,
          price: offerPrice,
          quantity: 1,
          commitment: `${value.termOfServiceNumber}`,
          process_type: processTypeName.toLowerCase(),
        };
      }

      productsList.push(product);
    });

    return productsList;
  }

  private cartViewCheckoutEvent(summaryPrices: ISummaryPrice[], apiSlots: SlotModel[], productsList: (ProductDevice | ProductOffer)[], orderSummary?: IOrderSummary.Root): void {
    const rawAddons: string[] = [];
    let valueWithoutDevicesFromApi: number = null;

    const offerPrices = summaryPrices.find(item => item.group === 'offer')?.prices;
    const offerPrice = +offerPrices?.find(price => +price.price[this.commonInfoService.taxName] > 0)?.price[this.commonInfoService.taxName];
    const activationFeePrices = +summaryPrices.find(item => item.group === 'activationFee')?.prices[0]?.price[this.commonInfoService.taxName];
    const devicesAndAccessoriesPrices = +summaryPrices.find(item => item.group === 'devicesAndAccessories')?.prices[0]?.price[this.commonInfoService.taxName];
    const oneTimeSumPrice = activationFeePrices ? activationFeePrices + devicesAndAccessoriesPrices : devicesAndAccessoriesPrices;

    if (!this.commonInfoService.b2c) {
      productsList.filter(product => !product['item_brand']).forEach(product => {
        valueWithoutDevicesFromApi += product.price;
      });
    }

    const value = valueWithoutDevicesFromApi ? valueWithoutDevicesFromApi : offerPrice || oneTimeSumPrice;
    const oneTimeValue = value === oneTimeSumPrice ? 0 : oneTimeSumPrice;
    apiSlots.forEach(slot => {
      if (slot._computedFields) {
        rawAddons.push(...slot._computedFields?.selectedAddons?.map(addon => addon.addonCode).filter(item => item));
      }
    });
    const addons = rawAddons.toString().replace(',', ';');

    const params = {
      event: orderSummary ? 'purchase' : window?.STEP === 1 ? 'view_cart' : 'begin_checkout',
      ecommerce: {
        cartId: this.configService.cartId || window.HELPER_DATA.cartId,
        currency: 'PLN',
        value,
        one_time_value: oneTimeValue,
        extraServices: addons,
        items: productsList
      }
    };

    if (orderSummary) {
      const deliveryCost = +orderSummary.deliveryCost?.[this.commonInfoService.taxName];

      Object.assign(params.ecommerce, {
        transaction_id: orderSummary?.crmdOrderId,
        affiliation: orderSummary?.affiliation ? orderSummary.affiliation : '',
        tax: 0,
        coupon: '',
        einvoice: orderSummary?.einvoice ? orderSummary?.einvoice : '',
        shipping: deliveryCost
      });
    }

    this.pushEvent(params);
  }

  private prepareEvent(
    eventName: string,
    data: DataLayerSlotService[],
    listName: string
  ): void {
    const productsList: any[] = [];
    /*
     *   'item_id': '4007496379', // id produktu
     *   'item_name': nazwa oferty - 'OFERTA M', // nazwa produktu
     *   'price': '70', // cena produktu / cena abonamentu
     *   'item_category': dla oferty: 'offer/voice/postpaid', // ścieżka kategorii produktu - productCategory/serviceCategory/serviceType
     *   'item_category': dla produktu: 'product'
     *   'quantity': 1, // liczba sztuk dodawanych do koszyka
     *   'process_type': 'ACQUISITION', // nazwa procesu
     *   'commitment': '24', // długość zobowiązania
     *   'currency': 'PLN',
     *   'item_brand': marka produktu - 3 i 4 litera sku
     *   'item_variant': kolor produktu - ostatnie 2 litery sku
     *   'one_time_fee': opłata początkowa
     */

    data.forEach((value: any, index: number) => {
      let product;
      let processTypeName = value.processType;

      if (value.processType === ProcessTypeEnum.OTHER_SALES) {
        processTypeName = 'OTHER_SALES';
      } else if (value.processType === ProcessTypeEnum.PREPAID_2_POSTPAID) {
        processTypeName = 'PREPAID2POSTPAID';
      }

      if (value.sku) {
        const pricePerPiece = value.pricePerPiece[TaxNameEnum.GROSS];
        const installmentPrice = value.installments?.filter(item => item.price);
        const installmentPriceMax = installmentPrice
          ? Math.max(...installmentPrice?.map(item => +item.price[this.commonInfoService.taxName]))
          : 0;
        const price = pricePerPiece ? pricePerPiece : installmentPriceMax;

        product = {
          item_id: this.getItemId(value.sku),
          item_name: value.name,
          item_brand: this.getItemBrand(value.sku),
          item_variant: this.getItemColor(value.sku),
          item_category: 'phone',
          price: Number(price),
          quantity: value.quantity,
          process_type: processTypeName.toLowerCase(),
          commitment: `${value.deviceTermLength}`,
          item_list_name: listName,
          one_time_fee: value.oneTimeFee.gross,
          currency: 'PLN',
        };
      } else {
        product = {
          item_id: value.offerId,
          item_name: value.offerName,
          price: Number(value.monthlyPrices[0].price[TaxNameEnum.GROSS]),
          item_category: `${value.bundleType}/${value.trafficType}/${value.serviceType}`,
          quantity: 1,
          process_type: processTypeName.toLowerCase(),
          commitment: `${value.termOfServiceNumber}`,
          item_list_name: listName,
          currency: 'PLN',
        };
      }

      productsList.push(product);
    });

    const params = {
      event: eventName,
      ecommerce: {}
    };

    params.ecommerce['items'] = productsList;


    this.pushEvent(params);
  }

  getItemId(id: string): string {
    return id.slice(0, -3);
  }

  getItemColor(sku: string): string {
    return sku.slice(-2);
  }

  getItemBrand(brand: string): string {
    return brand.slice(3, 5).toUpperCase();
  }

  setDeliveryPageEvent(rawValue: string, eventName: string): void {
    if (!(eventName && rawValue)) {
      return;
    }

    let params = {};
    let sameEventIndex;
    const value = rawValue.toLowerCase();

    switch (eventName) {
      case 'add_identity_verification_info':
        params = { method: value };
        break;
      case 'add_shipping_info':
        params = { shipping_tier: value };
        break;
      case 'add_payment_info':
        params = { payment_type: value };
        break;
    }

    if (this.configService.externalScriptsActive && this.configService.gtmScriptActive) {
      window.dataLayer = window.dataLayer || [];
      sameEventIndex = Array.isArray(window.dataLayer) ? window.dataLayer.findIndex(item => item['event'] === eventName) : undefined;

      if (sameEventIndex && sameEventIndex !== -1) {
        window.dataLayer?.splice(sameEventIndex, 1);
      }

      if (eventName === 'add_identity_verification_info') {
        window.dataLayer.push({
          event: eventName,
          method: params['method']
        });
      } else {
        window.dataLayer.push({
          event: eventName,
          ecommerce: params
        });
      }
    }
  }

  public deliveryAndPaymentOfferSets(): void {
    const offerSets = window.contractData?.innerHTML
      ? JSON.parse(window.contractData?.innerHTML).offerSets
      : '';

    const params = {
      event: 'segmenty',
    };

    offerSets.forEach(value => {
      const key = value.segment;

      params[key] = value.activationLimit.toString();
    });

    this.pushEvent(params);
  }

  private pushEvent(params): void {
    if (this.configService.externalScriptsActive && this.configService.gtmScriptActive) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(params);
    }
  }
}
